import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAddTechnicianModalStore } from './AddTechnicianModalStore';

import { TechnicianSiteFormValues, technicianSiteSchema } from 'api/clients/types';
import useAddTechnicianToObject from 'hooks/Object/useAddTechnicianToObject';
import useGetTechnicianToObject from 'hooks/Object/useGetTechnicianToObject';
import { MLSelect } from 'components/Select/Select';
import CreateTechnicianModal from 'pages/ClientDashboard/Modals/CreateTechnicianModal';
import GreenAddButton from 'components/Button/GreenAddButton';

interface AddTechnicianModalProps {
  refetchClients: () => void;
}

const AddTechnicianModal: FC<AddTechnicianModalProps> = ({ refetchClients }) => {
  const { AddTechnicianToObject } = useAddTechnicianToObject();
  const { isOpen, objectSlug, close } = useAddTechnicianModalStore();
  const { data: TechnicianList, refetch } = useGetTechnicianToObject(objectSlug);
  const [isOpenTechModal, setIsOpenTechModal] = useState<boolean>(false);
  const { t } = useTranslation();

  const { handleSubmit, control, reset } = useForm<TechnicianSiteFormValues>({
    resolver: zodResolver(technicianSiteSchema),
    defaultValues: {
      technician: '',
      is_lead: true,
    },
  });
  const onReset = () => {
    reset();
  };

  const onAddTechnician = (data: TechnicianSiteFormValues) => {
    AddTechnicianToObject(
      {
        technician: data,
        objectSlug,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-edit'), { variant: 'success' });
          close();
          onReset();
          refetchClients();
        },
        onError: (error: any) => {
          if (error.response.data.technician) {
            enqueueSnackbar(error.response.data.technician[0], { variant: 'error' });
          }
          close();
          onReset();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={close} size="md">
        <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={close}>
          {t('Modals.addTechnicianModal.title')}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center col-12">
            <form className="col-12">
              <div className="d-flex gap-2 justify-content-center align-items-end">
                <div className="flex-fill">
                  <Controller
                    control={control}
                    name="technician"
                    render={({ field, formState: { errors } }) => (
                      <MLSelect
                        {...field}
                        label={t('Modals.addTechnicianModal.technician')}
                        error={errors?.technician?.message}
                        value={field.value}
                        required
                        onChange={selectedOption => {
                          field.onChange(selectedOption);
                        }}
                        {...(!TechnicianList
                          ? {
                              disabled: true,
                              options: [{ value: '', label: t('Modals.addTechnicianModal.noTechnicians') }],
                            }
                          : {
                              options: [{ value: '', label: t('Modals.addTechnicianModal.selectTechnician') }].concat(
                                TechnicianList?.map(techPerson => ({
                                  value: techPerson.id.toString(),
                                  label: techPerson.label,
                                })),
                              ),
                            })}
                      />
                    )}
                  />
                </div>
                <GreenAddButton
                  title={t('Modals.manage-client.createNewTechnician')}
                  onClick={() => setIsOpenTechModal(true)}
                />
              </div>
              <Controller
                control={control}
                name="is_lead"
                render={({ field }) => (
                  <div className=" d-flex gap-2 mt-2">
                    <input
                      className="ml-1"
                      type="checkbox"
                      checked={field.value}
                      onChange={() => field.onChange(!field.value)}
                    />
                    <span className=" d-flex align-items-center ml-1">
                      {' '}
                      {t('Modals.addTechnicianModal.team-leader')}
                    </span>
                  </div>
                )}
              />
            </form>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-ghost-danger"
            onClick={() => {
              close();
              onReset();
            }}
          >
            {t('Modals.delete-modal.cancel')}
          </Button>{' '}
          <Button className="btn btn-ghost-success" onClick={handleSubmit(onAddTechnician)}>
            {t('Modals.addTechnicianModal.add')}
          </Button>
        </ModalFooter>
      </Modal>
      <CreateTechnicianModal
        isClientModal
        isOpen={isOpenTechModal}
        onClose={() => {
          setIsOpenTechModal(false);
          refetch();
        }}
      />
    </div>
  );
};

export default AddTechnicianModal;
